import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RightPanelContent, RightPanelMenuOption } from '../components/right-panel/right-panel.component';

@Injectable({
    providedIn: 'root'
})
export class RightPanelService {
    private readonly router = inject(Router);
    private readonly _isVisible$ = new BehaviorSubject<boolean>(false);
    private readonly _isExpanded$ = new BehaviorSubject<boolean>(false);
    private readonly _isCollapsed$ = new BehaviorSubject<boolean>(false);
    private readonly _content$ = new BehaviorSubject<RightPanelContent | null>(null);
    private readonly _showCloseButton$ = new BehaviorSubject<boolean>(true);
    private readonly _showExpandableButton$ = new BehaviorSubject<boolean>(true);

    public constructor() {
        // Reset panel state on navigation
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            // Reset to default state
            this._isExpanded$.next(false);
            this._isCollapsed$.next(false);

            // Hide the panel
            if (this._isVisible$.value) {
                this.hide();
            }
        });
    }

    public readonly isVisible$ = this._isVisible$.asObservable();
    public readonly isExpanded$ = this._isExpanded$.asObservable();
    public readonly isCollapsed$ = this._isCollapsed$.asObservable();
    public readonly content$ = this._content$.asObservable();
    public readonly showCloseButton$ = this._showCloseButton$.asObservable();
    public readonly showExpandableButton$ = this._showExpandableButton$.asObservable();

    public show(content: RightPanelContent) {
        //console.log('RightPanelService.show() called with:', content);
        this._content$.next(content);
        this._isVisible$.next(true);
        // Update showCloseButton if specified in content
        if (content.showCloseButton !== undefined) {
            this._showCloseButton$.next(content.showCloseButton);
        }
        // Update showExpandableButton if specified in content
        if (content.showExpandableButton !== undefined) {
            this._showExpandableButton$.next(content.showExpandableButton);
        }
        //console.log('Panel visibility set to true');
        if (this._isCollapsed$.value) {
            //console.log('Panel was collapsed, now expanding');
            this._isCollapsed$.next(false);
        }
    }

    public hide() {
        this._isVisible$.next(false);
        // Clear content after animation
        setTimeout(() => {
            this._content$.next(null);
        }, 300);
    }

    public toggle(content?: RightPanelContent) {
        if (this._isVisible$.value) {
            this.hide();
        } else if (content) {
            this.show(content);
        }
    }

    public setExpanded(expanded: boolean) {
        //console.log('RightPanelService: setExpanded called with:', expanded);
        this._isExpanded$.next(expanded);
        // If expanding, make sure we're not collapsed
        if (expanded) {
            this._isCollapsed$.next(false);
        }
    }

    public toggleExpanded() {
        this._isExpanded$.next(!this._isExpanded$.value);
        // If expanding, make sure we're not collapsed
        if (this._isExpanded$.value) {
            this._isCollapsed$.next(false);
        }
    }

    public setCollapsed(collapsed: boolean) {
        //console.log('RightPanelService: setCollapsed called with:', collapsed);
        this._isCollapsed$.next(collapsed);
        // If collapsing, make sure we're not expanded
        if (collapsed) {
            this._isExpanded$.next(false);
        }
    }

    public toggleCollapsed() {
        this._isCollapsed$.next(!this._isCollapsed$.value);
        // If collapsing, make sure we're not expanded
        if (this._isCollapsed$.value) {
            this._isExpanded$.next(false);
        }
    }

    public setShowCloseButton(show: boolean) {
        this._showCloseButton$.next(show);
    }

    public setShowExpandableButton(show: boolean) {
        this._showExpandableButton$.next(show);
    }

    // Utility methods for easier component usage
    public showComponent<T>(
        title: string,
        componentType: new (...args: unknown[]) => T,
        data?: Record<string, unknown>,
        showCloseButton?: boolean,
        showExpandableButton?: boolean,
        showCollapseToggle?: boolean,
        icon?: string
    ) {
        this.show({
            title,
            icon,
            component: componentType as RightPanelContent['component'],
            data,
            showCloseButton,
            showExpandableButton,
            showCollapseToggle
        });
    }

    // Show component with menu options
    public showComponentWithMenu<T>(
        title: string,
        componentType: new (...args: unknown[]) => T,
        data?: Record<string, unknown>,
        menuOptions?: RightPanelMenuOption[],
        showCloseButton?: boolean,
        showExpandableButton?: boolean,
        showCollapseToggle?: boolean,
        icon?: string
    ) {
        this.show({
            title,
            icon,
            component: componentType as RightPanelContent['component'],
            data,
            menuOptions,
            showCloseButton,
            showExpandableButton,
            showCollapseToggle
        });
    }

    // Quick method to show simple content
    public showContent(title: string, message: string) {
        this.show({
            title,
            data: { message }
        });
    }
}
